import { SketchLineObject, SketchArcObject, SketchEllipseObject, SketchSplineObject, SketchPointObject, SketchPointKind, SketchParabolaObject } from '../../mesh';
import { PointsMaterialFactory } from '../../material';
import { Vector3 } from 'three';
import type { ReferencePlane } from '../../reference';
import type { SketchIntersectionCandidate } from './SketchEntityIntersect';
import { SketchEntityIntersect } from './SketchEntityIntersect';
import type { IntersectJointContext, OffsetCandidate } from './type';

/**
 * 求交选取层的延伸方向枚举 —— 表示交点选取时沿实体位置参数前进的方向。
 *
 * 背景：旧实现用裸的 +1/-1 魔法数字表示延伸方向（线性参数 / 弧长 / 角度 /
 * 抛物线参数共用），语义不可读且易与其它"符号"变量（如偏移方向符号）混淆；
 * 本枚举将方向语义显式化，+1/-1 的数值转换仅收敛在
 * {@link SketchEntityNewControlPoint.directionSign} 一处。
 */
export enum IntersectDirection {
    /** 沿参数增大方向（线性参数 +t、弧长增大、角度逆时针、抛物线参数增大）。 */
    Forward = 'forward',
    /** 沿参数减小方向（线性参数 -t、弧长减小、角度顺时针、抛物线参数减小）。 */
    Backward = 'backward',
}

/**
 * 交点控制点编排与选取（②选取层）—— 承接 SketchEntityIntersect ①求解层
 * （compute*Intersections）返回的全部候选交点，供"延申-求交-截取"流程消费。
 * 职责划分：
 * 1. intersect*（编排层）：调用 SketchEntityIntersect.compute* 求解候选交点，
 *    依据接缝上下文（IntersectJointContext）计算延伸方向与出发点
 *    （dir/ref，线性参数/弧长/角度/抛物线参数），
 *    委托 pickFirst* 选取沿延伸方向最先相交者并包装为控制点；
 * 2. pickFirst*（选取层）：从全部候选交点中选取"离延伸端最近"的第一个交点并
 *    包装为独立 SketchPointObject 控制点实例；
 * 3. locateOriginStartLengthOnExtendedSpline（辅助）：定位 origin 样条起点在
 *    延伸后样条上的弧长位置（起点侧延伸的 ref 正确参照）。
 * 位置参数语义见 {@link SketchIntersectionCandidate}：直线 = 线段参数 t、样条 = 累计弧长、
 * 圆弧 = 相对圆心极角、椭圆弧 = 椭圆参数角、抛物线 = 抛物线参数。
 */
export class SketchEntityNewControlPoint {

    /**
     * 定位 origin 样条起点在延伸后样条曲线上的弧长位置。
     *
     * 实现说明：
     * 1. 起点侧延伸（sketchExtensionSpline header=true）在 curvePoints 头部前插
     *    延伸段（EXTEND_LENGTH=1000），原始起点附近被余弦衰减平滑，衰减因子
     *    cos(π/2)≈0 处的采样点与 origin 起点几何重合（误差 <1e-12 量级），
     *    故"距离 origin 起点最近的采样点"即原始起点在延伸后曲线上的位置；
     * 2. 该弧长是起点侧延伸（Backward）的正确参照：从断口（origin 起点）沿延伸方向
     *    最先相交的点 = length ≤ lengthRef 中最大者。若写死 0，延伸段内的交点会被
     *    全部过滤并落入"取最小者"兜底，延伸段双交点（延伸线穿过圆/椭圆/曲线）时
     *    会选到远端交点——即"弧起点与曲线起点相连时断口交点错误"的缺陷根因；
     * 3. 终点侧延伸（Forward）时 origin 终点在延伸后曲线上的弧长 = originTotalLength
     *    （原始段未被改动），无需此定位。
     *
     * @param extendedPoints 延伸后的样条采样点（世界坐标）。
     * @param originStart 原始样条起点（世界坐标）。
     * @returns origin 起点对应的弧长位置（从延伸后曲线起点累计）。
     */
    static locateOriginStartLengthOnExtendedSpline(
        extendedPoints: Vector3[],
        originStart: Vector3,
    ): number {
        // 累计弧长，并记录与 origin 起点距离最小的采样点对应的弧长
        let bestLength = 0;
        let bestDistSq = Number.POSITIVE_INFINITY;
        let accumulated = 0;
        for (let i = 0; i < extendedPoints.length; i++) {
            const distSq = extendedPoints[i]!.distanceToSquared(originStart);
            if (distSq < bestDistSq) {
                bestDistSq = distSq;
                bestLength = accumulated;
            }
            if (i < extendedPoints.length - 1) {
                accumulated += extendedPoints[i]!.distanceTo(extendedPoints[i + 1]!);
            }
        }
        return bestLength;
    }

    /**
     * 从全部候选交点中选取"沿线性位置参数延伸方向最先相交"的第一个交点并包装为控制点。
     * 实现说明：
     * 1. 无候选返回空数组，单候选直接包装返回；
     * 2. 多候选时按 dir 从 ref 出发选取最近者：Forward 取 parameter ≥ ref 中最小者，
     *    若全部在原始段内则取 parameter 最大者（最靠近延伸端）；Backward 对称；
     * 3. 位置参数语义随第一实体类型：直线 = 线段参数 t、样条 = 累计弧长、抛物线 = 抛物线参数。
     *
     * @param candidates 全部候选交点（compute*Intersections 的返回）。
     * @param dir 延伸方向（见 {@link IntersectDirection}）：Forward 沿参数增大方向，Backward 沿参数减小方向。
     * @param ref 延伸出发位置参数（origin 自由端在实体上的位置）。
     * @param referencePlane 参考面（用于交点控制点构造）。
     * @param isAuxiliary 交点控制点是否标记为辅助对象。
     * @returns 第一个交点控制点数组（至多 1 个；无交返回空数组）。
     */
    static pickFirstIntersectionByLinearParameter(
        candidates: SketchIntersectionCandidate[],
        dir: IntersectDirection,
        ref: number,
        referencePlane: ReferencePlane,
        isAuxiliary: boolean,
    ): SketchPointObject[] {
        // 无有效交点
        if (candidates.length === 0) {
            return [];
        }

        // 只有一个交点：直接返回
        if (candidates.length === 1) {
            const only = candidates[0]!;
            return [new SketchPointObject({
                position: only.worldPoint.clone(),
                pointKind: SketchPointKind.ControlPoint,
                material: PointsMaterialFactory.getSketchPointSpriteMaterial(),
                referencePlane,
                isAuxiliaryObject: isAuxiliary,
            })];
        }

        // 按延伸方向选择第一个交点：沿 dir 方向从 ref 出发最先相交的点
        let chosen = candidates[0]!;
        if (dir === IntersectDirection.Forward) {
            // 延伸方向 Forward（+parameter）：取 parameter ≥ ref 中最小者；若全部在原始段内，取 parameter 最大者（最靠近延伸端）
            const ahead = candidates.filter((item) => item.parameter >= ref - 1e-9);
            chosen = ahead.length > 0
                ? ahead.reduce((min, item) => (item.parameter < min.parameter ? item : min))
                : candidates.reduce((max, item) => (item.parameter > max.parameter ? item : max));
        } else {
            // 延伸方向 Backward（-parameter）：取 parameter ≤ ref 中最大者；若全部在原始段内，取 parameter 最小者
            const behind = candidates.filter((item) => item.parameter <= ref + 1e-9);
            chosen = behind.length > 0
                ? behind.reduce((max, item) => (item.parameter > max.parameter ? item : max))
                : candidates.reduce((min, item) => (item.parameter < min.parameter ? item : min));
        }

        // 返回第一个交点
        return [new SketchPointObject({
            position: chosen.worldPoint.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchPointSpriteMaterial(),
            referencePlane,
            isAuxiliaryObject: isAuxiliary,
        })];
    }

    /**
     * 从全部候选交点中选取"沿环形角度参数延伸方向最先相交"的第一个交点并包装为控制点。
     * 实现说明：
     * 1. 无候选返回空数组，单候选直接包装返回；
     * 2. 多候选时按有向角度距离（[0, 2π)，Forward 逆时针 / Backward 顺时针）取从 ref 出发最近者；
     * 3. 角度参数语义随第一实体类型：圆弧 = 相对圆心极角、椭圆弧 = 椭圆参数角。
     *
     * @param candidates 全部候选交点（compute*Intersections 的返回）。
     * @param dir 延伸方向（见 {@link IntersectDirection}）：Forward 角度增大方向（逆时针），Backward 角度减小方向（顺时针）。
     * @param ref 延伸出发角度（origin 自由端在实体上的极角/参数角）。
     * @param referencePlane 参考面（用于交点控制点构造）。
     * @param isAuxiliary 交点控制点是否标记为辅助对象。
     * @returns 第一个交点控制点数组（至多 1 个；无交返回空数组）。
     */
    static pickFirstIntersectionByAngularParameter(
        candidates: SketchIntersectionCandidate[],
        dir: IntersectDirection,
        ref: number,
        referencePlane: ReferencePlane,
        isAuxiliary: boolean,
    ): SketchPointObject[] {
        // 无有效交点
        if (candidates.length === 0) {
            return [];
        }

        // 只有一个交点：直接返回
        if (candidates.length === 1) {
            const only = candidates[0]!;
            return [new SketchPointObject({
                position: only.worldPoint.clone(),
                pointKind: SketchPointKind.ControlPoint,
                material: PointsMaterialFactory.getSketchPointSpriteMaterial(),
                referencePlane,
                isAuxiliaryObject: isAuxiliary,
            })];
        }

        // 计算从 from 沿弧方向（dir）到 to 的有向角度距离（[0, 2π)）
        // Forward 表示逆时针（角度增大方向），Backward 表示顺时针（角度减小方向）
        const angleDistance = (from: number, to: number, dir: IntersectDirection): number => {
            const TWO_PI = Math.PI * 2;
            if (dir === IntersectDirection.Forward) {
                return ((to - from) % TWO_PI + TWO_PI) % TWO_PI;
            }
            return ((from - to) % TWO_PI + TWO_PI) % TWO_PI;
        };

        // 沿延伸方向选择第一个交点：有向角度距离最小者
        let chosen = candidates[0]!;
        let bestDistance = angleDistance(ref, chosen.parameter, dir);
        for (let i = 1; i < candidates.length; i++) {
            const item = candidates[i]!;
            const distance = angleDistance(ref, item.parameter, dir);
            if (distance < bestDistance) {
                bestDistance = distance;
                chosen = item;
            }
        }

        // 返回第一个交点
        return [new SketchPointObject({
            position: chosen.worldPoint.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchPointSpriteMaterial(),
            referencePlane,
            isAuxiliaryObject: isAuxiliary,
        })];
    }

    /**
     * 线性参数实体（直线投影参数 / 样条累计弧长 / 抛物线参数）的"接缝侧"候选过滤。
     *
     * 背景：两实体闭合链偏移后，两条曲线可能在"另一接缝"（非相连端）交叉，
     * extend=false 求交会返回该交叉点（属于另一接缝而非当前接缝），导致用错误交点
     * 截取；本过滤剔除"比相连端更靠近非相连端"的交点（线性参数中点判定），
     * 使调用方回落延长求交（extend=true）求解真正的接缝交点。
     *
     * 过滤范围：
     * 1. 多候选（≥2）始终过滤，保证选中接缝侧交点；
     * 2. 单候选仅在两实体自封闭图形（isTwoEntityClosedLoop=true）时过滤——
     *    只有该场景存在"另一接缝交叉点"被误当作本接缝交点的风险；其它场景
     *    单候选几何上可信，直接返回，避免误删合法交点。
     *
     * @param candidates 全部候选交点。
     * @param jointParam 相连端在实体上的位置参数（延长前）。
     * @param freeParam 非相连端在实体上的位置参数（延长前）。
     * @param isTwoEntityClosedLoop 是否为两个草图实体组成的自封闭图形。
     * @returns 位于相连端一侧的候选交点。
     */
    private static filterLinearCandidatesByJointSide(
        candidates: SketchIntersectionCandidate[],
        jointParam: number,
        freeParam: number,
        isTwoEntityClosedLoop: boolean,
    ): SketchIntersectionCandidate[] {
        if (candidates.length === 0
            || (candidates.length === 1 && !isTwoEntityClosedLoop)) {
            return candidates;
        }
        // 线性参数下"接缝侧"即：交点到相连端的参数距离不大于到非相连端的距离
        return candidates.filter((candidate) =>
            Math.abs(candidate.parameter - jointParam) <= Math.abs(candidate.parameter - freeParam),
        );
    }

    /**
     * 环形角度参数实体（圆弧极角 / 椭圆参数角）的"接缝侧"候选过滤。
     *
     * 背景与 {@link filterLinearCandidatesByJointSide} 相同，但角度参数具有周期性，
     * 不能直接取绝对差，改为按"从自由端指向弧体内部的方向"测量交点到自由端的
     * 有向弧长：位于自由端半区（不足半弧体跨度）的交点属于另一接缝，剔除；
     * 位于延长区（越过相连端之外）的交点有向弧长超过弧体跨度，天然保留。
     * 过滤范围与 {@link filterLinearCandidatesByJointSide} 一致：多候选始终过滤，
     * 单候选仅在两实体自封闭图形时过滤。
     *
     * @param candidates 全部候选交点。
     * @param jointAngle 相连端角度（延长前）。
     * @param freeAngle 非相连端角度（延长前）。
     * @param arcDirection 弧扫掠方向（顺时针 = Backward，逆时针 = Forward）。
     * @param side 相连端。
     * @param isTwoEntityClosedLoop 是否为两个草图实体组成的自封闭图形。
     * @returns 位于相连端一侧的候选交点。
     */
    private static filterAngularCandidatesByJointSide(
        candidates: SketchIntersectionCandidate[],
        jointAngle: number,
        freeAngle: number,
        arcDirection: IntersectDirection,
        side: 'start' | 'end',
        isTwoEntityClosedLoop: boolean,
    ): SketchIntersectionCandidate[] {
        if (candidates.length === 0
            || (candidates.length === 1 && !isTwoEntityClosedLoop)) {
            return candidates;
        }
        const TWO_PI = Math.PI * 2;
        const norm = (angle: number): number => ((angle % TWO_PI) + TWO_PI) % TWO_PI;
        // 弧体起止角（相连端为 side 对应端）与弧体跨度
        const startAngle = side === 'start' ? jointAngle : freeAngle;
        const endAngle = side === 'start' ? freeAngle : jointAngle;
        const bodySpan = norm(
            SketchEntityNewControlPoint.directionSign(arcDirection) * (endAngle - startAngle),
        );
        // 从自由端指向弧体内部的方向：终点端相连时弧体位于自由端沿弧方向一侧，
        // 起点端相连时位于弧反方向一侧
        const fromFreeToBodyDirection = side === 'end'
            ? arcDirection
            : SketchEntityNewControlPoint.reverseDirection(arcDirection);
        const halfSpan = bodySpan / 2;
        return candidates.filter((candidate) => {
            // 交点沿"自由端→弧体内部"方向到自由端的有向弧长
            const distanceFromFree = norm(
                SketchEntityNewControlPoint.directionSign(fromFreeToBodyDirection)
                    * (candidate.parameter - freeAngle),
            );
            // 位于自由端半区 → 另一接缝交叉点，剔除
            return distanceFromFree >= halfSpan - 1e-9;
        });
    }

    /**
     * 将延伸方向转换为数值符号（+1 / -1）—— 仅用于环形角度参数的有向角度运算。
     * 这是方向枚举与数值计算的唯一转换收口点，业务代码不得再出现裸的 ±1 方向字面量。
     *
     * @param direction 延伸方向。
     * @returns Forward = +1，Backward = -1。
     */
    private static directionSign(direction: IntersectDirection): 1 | -1 {
        return direction === IntersectDirection.Forward ? 1 : -1;
    }

    /**
     * 取延伸方向的反向 —— 起点端/终点端的延伸方向互为反向，
     * 用于"从相连端背离弧身"与"弧扫掠方向"之间的转换。
     *
     * @param direction 延伸方向。
     * @returns 反向延伸方向。
     */
    private static reverseDirection(direction: IntersectDirection): IntersectDirection {
        return direction === IntersectDirection.Forward
            ? IntersectDirection.Backward
            : IntersectDirection.Forward;
    }

    static intersectLineLine(line1: SketchLineObject, line2: SketchLineObject): SketchPointObject[] {
        const referencePlane = line1.referencePlane;
        const isAuxiliary = line1.isAuxiliaryObject;

        // ① 求解候选交点（两条直线至多 1 个交点）
        const candidates = SketchEntityIntersect.computeLineLineIntersections(line1, line2);

        // ② 直线-直线无"延伸方向选取"问题：直接包装唯一候选
        if (candidates.length === 0) {
            return [];
        }
        const only = candidates[0]!;
        return [new SketchPointObject({
            position: only.worldPoint.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchPointSpriteMaterial(),
            referencePlane,
            isAuxiliaryObject: isAuxiliary,
        })];
    }

    static intersectLineArc(line: SketchLineObject, arc: SketchArcObject, lineJoint: IntersectJointContext,
        isTwoEntityClosedLoop: boolean,
    ): SketchPointObject[] {
        const referencePlane = line.referencePlane;
        const isAuxiliary = line.isAuxiliaryObject;

        // ① 求解全部候选交点（可见区间过滤与去重已在 compute 内完成）
        const candidates = SketchEntityIntersect.computeLineArcIntersections(line, arc);

        // ② 计算任意世界点投影到直线上的参数（用于定位接缝端/自由端在直线上的位置）
        const lineStartLocal = referencePlane.worldToLocal(line.startPoint.position);
        const lineEndLocal = referencePlane.worldToLocal(line.endPoint.position);
        const pdx = lineEndLocal.x - lineStartLocal.x;
        const pdy = lineEndLocal.y - lineStartLocal.y;
        const lineLenSq = pdx * pdx + pdy * pdy;
        const project = (worldPoint: Vector3): number => {
            const p = referencePlane.worldToLocal(worldPoint);
            // 退化直线（长度≈0）无方向可投影：返回 0，与旧行为保持一致
            return lineLenSq > 1e-24
                ? ((p.x - lineStartLocal.x) * pdx + (p.y - lineStartLocal.y) * pdy) / lineLenSq
                : 0;
        };

        // ③ 延伸方向与出发点由接缝上下文直接给出（不再靠 origin 端点匹配反推）：
        //    dir = Forward 沿 +t（终点侧）延伸，dir = Backward 沿 -t（起点侧）延伸；
        //    出发点 = 相连端端点在直线上的投影参数
        const dir: IntersectDirection = lineJoint.side === 'end'
            ? IntersectDirection.Forward
            : IntersectDirection.Backward;
        const jointT = project(lineJoint.jointEndWorld);
        const freeT = project(lineJoint.freeEndWorld);

        // ④ 接缝侧候选过滤：剔除位于非相连端一侧的交点（两实体闭合链在另一接缝
        //    交叉时，extend=false 会返回错误连接的交叉点，必须剔除后回落延长求交）
        const onJointSideCandidates = SketchEntityNewControlPoint.filterLinearCandidatesByJointSide(
            candidates, jointT, freeT, isTwoEntityClosedLoop,
        );

        // ⑤ 按延伸方向选取第一个交点并包装为控制点
        return SketchEntityNewControlPoint.pickFirstIntersectionByLinearParameter(
            onJointSideCandidates, dir, jointT, referencePlane, isAuxiliary,
        );
    }

    static intersectLineEllipse(line: SketchLineObject, ellipse: SketchEllipseObject, lineJoint: IntersectJointContext,
        isTwoEntityClosedLoop: boolean,
    ): SketchPointObject[] {
        const referencePlane = line.referencePlane;
        const isAuxiliary = line.isAuxiliaryObject;

        // ① 求解全部候选交点（可见区间过滤与去重已在 compute 内完成）
        const candidates = SketchEntityIntersect.computeLineEllipseIntersections(line, ellipse);

        // ② 计算任意世界点投影到直线上的参数（用于定位接缝端/自由端在直线上的位置）
        const lineStartLocal = referencePlane.worldToLocal(line.startPoint.position);
        const lineEndLocal = referencePlane.worldToLocal(line.endPoint.position);
        const pdx = lineEndLocal.x - lineStartLocal.x;
        const pdy = lineEndLocal.y - lineStartLocal.y;
        const lineLenSq = pdx * pdx + pdy * pdy;
        const project = (worldPoint: Vector3): number => {
            const p = referencePlane.worldToLocal(worldPoint);
            // 退化直线（长度≈0）无方向可投影：返回 0，与旧行为保持一致
            return lineLenSq > 1e-24
                ? ((p.x - lineStartLocal.x) * pdx + (p.y - lineStartLocal.y) * pdy) / lineLenSq
                : 0;
        };

        // ③ 延伸方向与出发点由接缝上下文直接给出（不再靠 origin 端点匹配反推）：
        //    dir = Forward 沿 +t（终点侧）延伸，dir = Backward 沿 -t（起点侧）延伸；
        //    出发点 = 相连端端点在直线上的投影参数
        const dir: IntersectDirection = lineJoint.side === 'end'
            ? IntersectDirection.Forward
            : IntersectDirection.Backward;
        const jointT = project(lineJoint.jointEndWorld);
        const freeT = project(lineJoint.freeEndWorld);

        // ④ 接缝侧候选过滤：剔除位于非相连端一侧的交点
        const onJointSideCandidates = SketchEntityNewControlPoint.filterLinearCandidatesByJointSide(
            candidates, jointT, freeT, isTwoEntityClosedLoop,
        );

        // ⑤ 按延伸方向选取第一个交点并包装为控制点
        return SketchEntityNewControlPoint.pickFirstIntersectionByLinearParameter(
            onJointSideCandidates, dir, jointT, referencePlane, isAuxiliary,
        );
    }

    static intersectLineSpline(line: SketchLineObject, spline: SketchSplineObject, splineJoint: IntersectJointContext,
        isTwoEntityClosedLoop: boolean,
    ): SketchPointObject[] {
        const referencePlane = line.referencePlane;
        const isAuxiliary = line.isAuxiliaryObject;

        // ① 求解全部候选交点（可见区间过滤与去重已在 compute 内完成；
        //    参数 = 沿样条的累计弧长）
        const candidates = SketchEntityIntersect.computeLineSplineIntersections(line, spline);

        // ② 计算原始样条总弧长（用于定位延伸出发端与接缝侧过滤）
        const originPoints = splineJoint.originCurvePoints ?? spline.getCurvePoints();
        let originTotalLength = 0;
        for (let i = 0; i < originPoints.length - 1; i++) {
            const p0 = referencePlane.worldToLocal(originPoints[i]!);
            const p1 = referencePlane.worldToLocal(originPoints[i + 1]!);
            const pdx = p1.x - p0.x;
            const pdy = p1.y - p0.y;
            originTotalLength += Math.sqrt(pdx * pdx + pdy * pdy);
        }

        // ③ 延伸方向与出发点由接缝上下文直接给出（不再靠 origin 端点匹配反推）：
        //    dir = Forward 沿弧长增大方向（终点侧）延伸，dir = Backward 沿弧长减小方向（起点侧）延伸
        const dir: IntersectDirection = splineJoint.side === 'end'
            ? IntersectDirection.Forward
            : IntersectDirection.Backward;
        const curvePoints = spline.getCurvePoints();
        let jointLength: number;
        let freeLength: number;
        let ref: number;
        if (splineJoint.side === 'end') {
            // 终点侧：相连端 = 原始终点（弧长 = 原始总弧长），自由端 = 原始起点（弧长 0）
            jointLength = originTotalLength;
            freeLength = 0;
            ref = originTotalLength;
        } else {
            // 起点侧：相连端 = 原始起点，其弧长必须在"延伸后"样条上定位——
            // 起点侧延伸在曲线头部前插延伸段，写死 0 会过滤掉延伸段内交点
            // （断口交点错误缺陷根因，见 locateOriginStartLengthOnExtendedSpline 注释）
            const originStartWorld = originPoints[0]!;
            jointLength = SketchEntityNewControlPoint.locateOriginStartLengthOnExtendedSpline(
                curvePoints, originStartWorld,
            );
            freeLength = jointLength + originTotalLength;
            ref = jointLength;
        }

        // ④ 接缝侧候选过滤：剔除位于非相连端一侧的交点
        const onJointSideCandidates = SketchEntityNewControlPoint.filterLinearCandidatesByJointSide(
            candidates, jointLength, freeLength, isTwoEntityClosedLoop,
        );

        // ⑤ 按延伸方向选取第一个交点并包装为控制点
        return SketchEntityNewControlPoint.pickFirstIntersectionByLinearParameter(
            onJointSideCandidates, dir, ref, referencePlane, isAuxiliary,
        );
    }

    static intersectLineParabola(line: SketchLineObject, parabola: SketchParabolaObject, lineJoint: IntersectJointContext,
        isTwoEntityClosedLoop: boolean,
    ): SketchPointObject[] {
        const referencePlane = line.referencePlane;
        const isAuxiliary = line.isAuxiliaryObject;

        // ① 求解全部候选交点（可见区间过滤与去重已在 compute 内完成）
        const candidates = SketchEntityIntersect.computeLineParabolaIntersections(line, parabola);

        // ② 计算任意世界点投影到直线上的参数（用于定位接缝端/自由端在直线上的位置）
        const lineStartLocal = referencePlane.worldToLocal(line.startPoint.position);
        const lineEndLocal = referencePlane.worldToLocal(line.endPoint.position);
        const pdx = lineEndLocal.x - lineStartLocal.x;
        const pdy = lineEndLocal.y - lineStartLocal.y;
        const lineLenSq = pdx * pdx + pdy * pdy;
        const project = (worldPoint: Vector3): number => {
            const p = referencePlane.worldToLocal(worldPoint);
            // 退化直线（长度≈0）无方向可投影：返回 0，与旧行为保持一致
            return lineLenSq > 1e-24
                ? ((p.x - lineStartLocal.x) * pdx + (p.y - lineStartLocal.y) * pdy) / lineLenSq
                : 0;
        };

        // ③ 延伸方向与出发点由接缝上下文直接给出：
        //    dir = +1 沿 +t（终点侧）延伸，dir = -1 沿 -t（起点侧）延伸；
        //    出发点 = 相连端端点在直线上的投影参数
        const dir: IntersectDirection = lineJoint.side === 'end'
            ? IntersectDirection.Forward
            : IntersectDirection.Backward;
        const jointT = project(lineJoint.jointEndWorld);
        const freeT = project(lineJoint.freeEndWorld);

        // ④ 接缝侧候选过滤：剔除位于非相连端一侧的交点
        const onJointSideCandidates = SketchEntityNewControlPoint.filterLinearCandidatesByJointSide(
            candidates, jointT, freeT, isTwoEntityClosedLoop,
        );

        // ⑤ 按延伸方向选取第一个交点并包装为控制点
        return SketchEntityNewControlPoint.pickFirstIntersectionByLinearParameter(
            onJointSideCandidates, dir, jointT, referencePlane, isAuxiliary,
        );
    }

    static intersectArcLine(arc: SketchArcObject, line: SketchLineObject, lineJoint: IntersectJointContext,
        isTwoEntityClosedLoop: boolean,
    ): SketchPointObject[] {
        return SketchEntityNewControlPoint.intersectLineArc(line, arc, lineJoint, isTwoEntityClosedLoop);
    }

    static intersectArcArc(arc1: SketchArcObject, arc2: SketchArcObject, arc1Joint: IntersectJointContext,
        isTwoEntityClosedLoop: boolean,
    ): SketchPointObject[] {
        const referencePlane = arc1.referencePlane;
        // 圆弧对象无 isAuxiliaryObject 协议（该属性仅线段/控制点持有），交点控制点默认非辅助对象
        const isAuxiliary = false;

        // ① 求解全部候选交点（可见区间过滤与去重已在 compute 内完成；
        //    参数 = 交点相对 arc1 圆心的极角）
        const candidates = SketchEntityIntersect.computeArcArcIntersections(arc1, arc2);

        // ② 延伸方向与出发点由接缝上下文直接给出（不再靠 origin 端点匹配反推）：
        //    弧方向（IntersectDirection）：clockwise=false 为 Forward（角度增大），
        //    true 为 Backward（角度减小）；延伸方向 = 从相连端背离弧身的方向
        const centerLocal = referencePlane.worldToLocal(arc1.getArcDefinition().centerWorld.clone());
        const clockwise = arc1.getArcDefinition().clockwise;
        const arcDirection: IntersectDirection = clockwise
            ? IntersectDirection.Backward
            : IntersectDirection.Forward;
        const dir: IntersectDirection = arc1Joint.side === 'start'
            ? SketchEntityNewControlPoint.reverseDirection(arcDirection)
            : arcDirection;
        const angleOf = (worldPoint: Vector3): number => {
            const local = referencePlane.worldToLocal(worldPoint);
            return Math.atan2(local.y - centerLocal.y, local.x - centerLocal.x);
        };
        const jointAngle = angleOf(arc1Joint.jointEndWorld);
        const freeAngle = angleOf(arc1Joint.freeEndWorld);

        // ③ 接缝侧候选过滤：剔除位于非相连端一侧的交点
        const onJointSideCandidates = SketchEntityNewControlPoint.filterAngularCandidatesByJointSide(
            candidates, jointAngle, freeAngle, arcDirection, arc1Joint.side, isTwoEntityClosedLoop,
        );

        // ④ 按延伸方向选取第一个交点并包装为控制点
        return SketchEntityNewControlPoint.pickFirstIntersectionByAngularParameter(
            onJointSideCandidates, dir, jointAngle, referencePlane, isAuxiliary,
        );
    }

    static intersectArcEllipse(arc: SketchArcObject, ellipse: SketchEllipseObject, arcJoint: IntersectJointContext,
        isTwoEntityClosedLoop: boolean,
    ): SketchPointObject[] {
        const referencePlane = arc.referencePlane;
        // 圆弧对象无 isAuxiliaryObject 协议（该属性仅线段/控制点持有），交点控制点默认非辅助对象
        const isAuxiliary = false;

        // ① 求解全部候选交点（可见区间过滤与去重已在 compute 内完成；
        //    参数 = 交点相对 arc 圆心的极角）
        const candidates = SketchEntityIntersect.computeArcEllipseIntersections(arc, ellipse);

        // ② 延伸方向与出发点由接缝上下文直接给出（不再靠 origin 端点匹配反推）：
        //    弧方向（IntersectDirection）：clockwise=false 为 Forward（角度增大），
        //    true 为 Backward（角度减小）；延伸方向 = 从相连端背离弧身的方向
        const centerLocal = referencePlane.worldToLocal(arc.getArcDefinition().centerWorld.clone());
        const clockwise = arc.getArcDefinition().clockwise;
        const arcDirection: IntersectDirection = clockwise
            ? IntersectDirection.Backward
            : IntersectDirection.Forward;
        const dir: IntersectDirection = arcJoint.side === 'start'
            ? SketchEntityNewControlPoint.reverseDirection(arcDirection)
            : arcDirection;
        const angleOf = (worldPoint: Vector3): number => {
            const local = referencePlane.worldToLocal(worldPoint);
            return Math.atan2(local.y - centerLocal.y, local.x - centerLocal.x);
        };
        const jointAngle = angleOf(arcJoint.jointEndWorld);
        const freeAngle = angleOf(arcJoint.freeEndWorld);

        // ③ 接缝侧候选过滤：剔除位于非相连端一侧的交点
        const onJointSideCandidates = SketchEntityNewControlPoint.filterAngularCandidatesByJointSide(
            candidates, jointAngle, freeAngle, arcDirection, arcJoint.side, isTwoEntityClosedLoop,
        );

        // ④ 按延伸方向选取第一个交点并包装为控制点
        return SketchEntityNewControlPoint.pickFirstIntersectionByAngularParameter(
            onJointSideCandidates, dir, jointAngle, referencePlane, isAuxiliary,
        );
    }

    static intersectArcSpline(arc: SketchArcObject, spline: SketchSplineObject, splineJoint: IntersectJointContext,
        isTwoEntityClosedLoop: boolean,
    ): SketchPointObject[] {
        const referencePlane = arc.referencePlane;
        // 圆弧对象无 isAuxiliaryObject 协议（该属性仅线段/控制点持有），交点控制点默认非辅助对象
        const isAuxiliary = false;

        // ① 求解全部候选交点（可见区间过滤与去重已在 compute 内完成；
        //    参数 = 沿样条的累计弧长）
        const candidates = SketchEntityIntersect.computeArcSplineIntersections(arc, spline);

        // ② 计算原始样条总弧长（用于定位延伸出发端与接缝侧过滤）
        const originPoints = splineJoint.originCurvePoints ?? spline.getCurvePoints();
        let originTotalLength = 0;
        for (let i = 0; i < originPoints.length - 1; i++) {
            const p0 = referencePlane.worldToLocal(originPoints[i]!);
            const p1 = referencePlane.worldToLocal(originPoints[i + 1]!);
            const pdx = p1.x - p0.x;
            const pdy = p1.y - p0.y;
            originTotalLength += Math.sqrt(pdx * pdx + pdy * pdy);
        }

        // ③ 延伸方向与出发点由接缝上下文直接给出（不再靠 origin 端点匹配反推）：
        //    dir = Forward 沿弧长增大方向（终点侧）延伸，dir = Backward 沿弧长减小方向（起点侧）延伸
        const dir: IntersectDirection = splineJoint.side === 'end'
            ? IntersectDirection.Forward
            : IntersectDirection.Backward;
        const curvePoints = spline.getCurvePoints();
        let jointLength: number;
        let freeLength: number;
        let ref: number;
        if (splineJoint.side === 'end') {
            // 终点侧：相连端 = 原始终点（弧长 = 原始总弧长），自由端 = 原始起点（弧长 0）
            jointLength = originTotalLength;
            freeLength = 0;
            ref = originTotalLength;
        } else {
            // 起点侧：相连端 = 原始起点，其弧长必须在"延伸后"样条上定位——
            // 起点侧延伸在曲线头部前插延伸段，写死 0 会过滤掉延伸段内交点
            const originStartWorld = originPoints[0]!;
            jointLength = SketchEntityNewControlPoint.locateOriginStartLengthOnExtendedSpline(
                curvePoints, originStartWorld,
            );
            freeLength = jointLength + originTotalLength;
            ref = jointLength;
        }

        // ④ 接缝侧候选过滤：剔除位于非相连端一侧的交点
        const onJointSideCandidates = SketchEntityNewControlPoint.filterLinearCandidatesByJointSide(
            candidates, jointLength, freeLength, isTwoEntityClosedLoop,
        );

        // ⑤ 按延伸方向选取第一个交点并包装为控制点
        return SketchEntityNewControlPoint.pickFirstIntersectionByLinearParameter(
            onJointSideCandidates, dir, ref, referencePlane, isAuxiliary,
        );
    }

    static intersectArcParabola(arc: SketchArcObject, parabola: SketchParabolaObject, parabolaJoint: IntersectJointContext,
        isTwoEntityClosedLoop: boolean,
    ): SketchPointObject[] {
        const referencePlane = arc.referencePlane;
        // 圆弧对象无 isAuxiliaryObject 协议（该属性仅线段/控制点持有），交点控制点默认非辅助对象
        const isAuxiliary = false;

        // ① 求解全部候选交点（可见区间过滤与去重已在 compute 内完成；
        //    参数 = 抛物线参数 t）
        const candidates = SketchEntityIntersect.computeArcParabolaIntersections(arc, parabola);

        // ② 延伸方向与出发点由接缝上下文直接给出（不再靠 origin 端点匹配反推）：
        //    延伸方向由参数区间走向决定：spanDirection = Forward（end ≥ start）时终点侧沿参数
        //    增大方向、起点侧沿参数减小方向；端点反向越过（spanDirection = Backward）时相反。
        //    注意：spanDirection 由当前（可能已延长）抛物线定义计算，延长只按区间走向
        //    增减参数，不改变区间走向符号
        const definition = parabola.getParabolaDefinition();
        const spanDirection: IntersectDirection =
            definition.endParameter >= definition.startParameter
                ? IntersectDirection.Forward
                : IntersectDirection.Backward;
        const dir: IntersectDirection = parabolaJoint.side === 'end'
            ? spanDirection
            : SketchEntityNewControlPoint.reverseDirection(spanDirection);
        // 原始起止参数（延长前；未提供时回退到当前定义）
        const originStart = parabolaJoint.originStartParameter ?? definition.startParameter;
        const originEnd = parabolaJoint.originEndParameter ?? definition.endParameter;
        const jointParam = parabolaJoint.side === 'end' ? originEnd : originStart;
        const freeParam = parabolaJoint.side === 'end' ? originStart : originEnd;

        // ③ 接缝侧候选过滤：剔除位于非相连端一侧的交点
        const onJointSideCandidates = SketchEntityNewControlPoint.filterLinearCandidatesByJointSide(
            candidates, jointParam, freeParam, isTwoEntityClosedLoop,
        );

        // ④ 按延伸方向选取第一个交点并包装为控制点
        return SketchEntityNewControlPoint.pickFirstIntersectionByLinearParameter(
            onJointSideCandidates, dir, jointParam, referencePlane, isAuxiliary,
        );
    }

    static intersectEllipseLine(ellipse: SketchEllipseObject, line: SketchLineObject, lineJoint: IntersectJointContext,
        isTwoEntityClosedLoop: boolean,
    ): SketchPointObject[] {
        return SketchEntityNewControlPoint.intersectLineEllipse(line, ellipse, lineJoint, isTwoEntityClosedLoop);
    }

    static intersectEllipseArc(ellipse: SketchEllipseObject, arc: SketchArcObject, arcJoint: IntersectJointContext,
        isTwoEntityClosedLoop: boolean,
    ): SketchPointObject[] {
        return SketchEntityNewControlPoint.intersectArcEllipse(arc, ellipse, arcJoint, isTwoEntityClosedLoop);
    }

    static intersectEllipseEllipse(ellipse1: SketchEllipseObject, ellipse2: SketchEllipseObject, ellipse1Joint: IntersectJointContext,
        isTwoEntityClosedLoop: boolean,
    ): SketchPointObject[] {
        const referencePlane = ellipse1.referencePlane;
        // 椭圆弧对象无 isAuxiliaryObject 协议（该属性仅线段/控制点持有），交点控制点默认非辅助对象
        const isAuxiliary = false;

        // ① 求解全部候选交点（可见区间过滤与去重已在 compute 内完成；
        //    参数 = 交点相对 ellipse1 的椭圆参数角 θ）
        const candidates = SketchEntityIntersect.computeEllipseEllipseIntersections(ellipse1, ellipse2);

        // ② 延伸方向与出发点由接缝上下文直接给出（不再靠 origin 端点匹配反推）：
        //    弧方向（IntersectDirection）：clockwise=false 为 Forward（参数角增大），
        //    true 为 Backward（参数角减小）；延伸方向 = 从相连端背离弧身的方向
        const ellipseDef = ellipse1.getEllipseDefinition();
        const centerLocal = referencePlane.worldToLocal(ellipseDef.centerWorld.clone());
        const { radiusX, radiusY, rotation, clockwise } = ellipseDef;
        const cr = Math.cos(rotation);
        const sr = Math.sin(rotation);
        const arcDirection: IntersectDirection = clockwise
            ? IntersectDirection.Backward
            : IntersectDirection.Forward;
        const dir: IntersectDirection = ellipse1Joint.side === 'start'
            ? SketchEntityNewControlPoint.reverseDirection(arcDirection)
            : arcDirection;
        const thetaOf = (worldPoint: Vector3): number => {
            const local = referencePlane.worldToLocal(worldPoint);
            const qx = local.x - centerLocal.x;
            const qy = local.y - centerLocal.y;
            const xPrime = qx * cr + qy * sr;
            const yPrime = -qx * sr + qy * cr;
            return Math.atan2(yPrime / radiusY, xPrime / radiusX);
        };
        const jointTheta = thetaOf(ellipse1Joint.jointEndWorld);
        const freeTheta = thetaOf(ellipse1Joint.freeEndWorld);

        // ③ 接缝侧候选过滤：剔除位于非相连端一侧的交点
        const onJointSideCandidates = SketchEntityNewControlPoint.filterAngularCandidatesByJointSide(
            candidates, jointTheta, freeTheta, arcDirection, ellipse1Joint.side, isTwoEntityClosedLoop,
        );

        // ④ 按延伸方向选取第一个交点并包装为控制点
        return SketchEntityNewControlPoint.pickFirstIntersectionByAngularParameter(
            onJointSideCandidates, dir, jointTheta, referencePlane, isAuxiliary,
        );
    }

    static intersectEllipseSpline(ellipse: SketchEllipseObject, spline: SketchSplineObject, splineJoint: IntersectJointContext,
        isTwoEntityClosedLoop: boolean,
    ): SketchPointObject[] {
        const referencePlane = ellipse.referencePlane;
        // 椭圆弧对象无 isAuxiliaryObject 协议（该属性仅线段/控制点持有），交点控制点默认非辅助对象
        const isAuxiliary = false;

        // ① 求解全部候选交点（可见区间过滤与去重已在 compute 内完成；
        //    参数 = 沿样条的累计弧长）
        const candidates = SketchEntityIntersect.computeEllipseSplineIntersections(ellipse, spline);

        // ② 计算原始样条总弧长（用于定位延伸出发端与接缝侧过滤）
        const originPoints = splineJoint.originCurvePoints ?? spline.getCurvePoints();
        let originTotalLength = 0;
        for (let i = 0; i < originPoints.length - 1; i++) {
            const p0 = referencePlane.worldToLocal(originPoints[i]!);
            const p1 = referencePlane.worldToLocal(originPoints[i + 1]!);
            const pdx = p1.x - p0.x;
            const pdy = p1.y - p0.y;
            originTotalLength += Math.sqrt(pdx * pdx + pdy * pdy);
        }

        // ③ 延伸方向与出发点由接缝上下文直接给出（不再靠 origin 端点匹配反推）：
        //    dir = Forward 沿弧长增大方向（终点侧）延伸，dir = Backward 沿弧长减小方向（起点侧）延伸
        const dir: IntersectDirection = splineJoint.side === 'end'
            ? IntersectDirection.Forward
            : IntersectDirection.Backward;
        const curvePoints = spline.getCurvePoints();
        let jointLength: number;
        let freeLength: number;
        let ref: number;
        if (splineJoint.side === 'end') {
            // 终点侧：相连端 = 原始终点（弧长 = 原始总弧长），自由端 = 原始起点（弧长 0）
            jointLength = originTotalLength;
            freeLength = 0;
            ref = originTotalLength;
        } else {
            // 起点侧：相连端 = 原始起点，其弧长必须在"延伸后"样条上定位——
            // 起点侧延伸在曲线头部前插延伸段，写死 0 会过滤掉延伸段内交点
            const originStartWorld = originPoints[0]!;
            jointLength = SketchEntityNewControlPoint.locateOriginStartLengthOnExtendedSpline(
                curvePoints, originStartWorld,
            );
            freeLength = jointLength + originTotalLength;
            ref = jointLength;
        }

        // ④ 接缝侧候选过滤：剔除位于非相连端一侧的交点
        const onJointSideCandidates = SketchEntityNewControlPoint.filterLinearCandidatesByJointSide(
            candidates, jointLength, freeLength, isTwoEntityClosedLoop,
        );

        // ⑤ 按延伸方向选取第一个交点并包装为控制点
        return SketchEntityNewControlPoint.pickFirstIntersectionByLinearParameter(
            onJointSideCandidates, dir, ref, referencePlane, isAuxiliary,
        );
    }

    static intersectEllipseParabola(ellipse: SketchEllipseObject, parabola: SketchParabolaObject, parabolaJoint: IntersectJointContext,
        isTwoEntityClosedLoop: boolean,
    ): SketchPointObject[] {
        const referencePlane = ellipse.referencePlane;
        // 椭圆弧对象无 isAuxiliaryObject 协议（该属性仅线段/控制点持有），交点控制点默认非辅助对象
        const isAuxiliary = false;

        // ① 求解全部候选交点（可见区间过滤与去重已在 compute 内完成；
        //    参数 = 抛物线参数 t）
        const candidates = SketchEntityIntersect.computeEllipseParabolaIntersections(ellipse, parabola);

        // ② 延伸方向与出发点由接缝上下文直接给出（不再靠 origin 端点匹配反推）：
        //    延伸方向由参数区间走向决定：spanDirection = Forward（end ≥ start）时终点侧沿参数
        //    增大方向、起点侧沿参数减小方向；端点反向越过（spanDirection = Backward）时相反。
        //    注意：spanDirection 由当前（可能已延长）抛物线定义计算，延长只按区间走向
        //    增减参数，不改变区间走向符号
        const definition = parabola.getParabolaDefinition();
        const spanDirection: IntersectDirection =
            definition.endParameter >= definition.startParameter
                ? IntersectDirection.Forward
                : IntersectDirection.Backward;
        const dir: IntersectDirection = parabolaJoint.side === 'end'
            ? spanDirection
            : SketchEntityNewControlPoint.reverseDirection(spanDirection);
        // 原始起止参数（延长前；未提供时回退到当前定义）
        const originStart = parabolaJoint.originStartParameter ?? definition.startParameter;
        const originEnd = parabolaJoint.originEndParameter ?? definition.endParameter;
        const jointParam = parabolaJoint.side === 'end' ? originEnd : originStart;
        const freeParam = parabolaJoint.side === 'end' ? originStart : originEnd;

        // ③ 接缝侧候选过滤：剔除位于非相连端一侧的交点
        const onJointSideCandidates = SketchEntityNewControlPoint.filterLinearCandidatesByJointSide(
            candidates, jointParam, freeParam, isTwoEntityClosedLoop,
        );

        // ④ 按延伸方向选取第一个交点并包装为控制点
        return SketchEntityNewControlPoint.pickFirstIntersectionByLinearParameter(
            onJointSideCandidates, dir, jointParam, referencePlane, isAuxiliary,
        );
    }

    static intersectSplineLine(spline: SketchSplineObject, line: SketchLineObject, splineJoint: IntersectJointContext,
        isTwoEntityClosedLoop: boolean,
    ): SketchPointObject[] {
        return SketchEntityNewControlPoint.intersectLineSpline(line, spline, splineJoint, isTwoEntityClosedLoop);
    }

    static intersectSplineArc(spline: SketchSplineObject, arc: SketchArcObject, splineJoint: IntersectJointContext,
        isTwoEntityClosedLoop: boolean,
    ): SketchPointObject[] {
        return SketchEntityNewControlPoint.intersectArcSpline(arc, spline, splineJoint, isTwoEntityClosedLoop);
    }

    static intersectSplineEllipse(spline: SketchSplineObject, ellipse: SketchEllipseObject, splineJoint: IntersectJointContext,
        isTwoEntityClosedLoop: boolean,
    ): SketchPointObject[] {
        return SketchEntityNewControlPoint.intersectEllipseSpline(ellipse, spline, splineJoint, isTwoEntityClosedLoop);
    }

    static intersectSplineSpline(spline1: SketchSplineObject, spline2: SketchSplineObject, spline1Joint: IntersectJointContext,
        isTwoEntityClosedLoop: boolean,
    ): SketchPointObject[] {
        const referencePlane = spline1.referencePlane;
        // 样条对象无 isAuxiliaryObject 协议（该属性仅线段/控制点持有），交点控制点默认非辅助对象
        const isAuxiliary = false;

        // ① 求解全部候选交点（可见区间过滤与去重已在 compute 内完成；
        //    参数 = 沿 spline1 的累计弧长）
        const candidates = SketchEntityIntersect.computeSplineSplineIntersections(spline1, spline2);

        // ② 计算原始样条总弧长（用于定位延伸出发端与接缝侧过滤）
        const originPoints = spline1Joint.originCurvePoints ?? spline1.getCurvePoints();
        let originTotalLength = 0;
        for (let i = 0; i < originPoints.length - 1; i++) {
            const p0 = referencePlane.worldToLocal(originPoints[i]!);
            const p1 = referencePlane.worldToLocal(originPoints[i + 1]!);
            const pdx = p1.x - p0.x;
            const pdy = p1.y - p0.y;
            originTotalLength += Math.sqrt(pdx * pdx + pdy * pdy);
        }

        // ③ 延伸方向与出发点由接缝上下文直接给出（不再靠 origin 端点匹配反推）：
        //    dir = Forward 沿弧长增大方向（终点侧）延伸，dir = Backward 沿弧长减小方向（起点侧）延伸
        const dir: IntersectDirection = spline1Joint.side === 'end'
            ? IntersectDirection.Forward
            : IntersectDirection.Backward;
        const curvePoints = spline1.getCurvePoints();
        let jointLength: number;
        let freeLength: number;
        let ref: number;
        if (spline1Joint.side === 'end') {
            // 终点侧：相连端 = 原始终点（弧长 = 原始总弧长），自由端 = 原始起点（弧长 0）
            jointLength = originTotalLength;
            freeLength = 0;
            ref = originTotalLength;
        } else {
            // 起点侧：相连端 = 原始起点，其弧长必须在"延伸后"样条上定位——
            // 起点侧延伸在曲线头部前插延伸段，写死 0 会过滤掉延伸段内交点
            const originStartWorld = originPoints[0]!;
            jointLength = SketchEntityNewControlPoint.locateOriginStartLengthOnExtendedSpline(
                curvePoints, originStartWorld,
            );
            freeLength = jointLength + originTotalLength;
            ref = jointLength;
        }

        // ④ 接缝侧候选过滤：剔除位于非相连端一侧的交点
        const onJointSideCandidates = SketchEntityNewControlPoint.filterLinearCandidatesByJointSide(
            candidates, jointLength, freeLength, isTwoEntityClosedLoop,
        );

        // ⑤ 按延伸方向选取第一个交点并包装为控制点
        return SketchEntityNewControlPoint.pickFirstIntersectionByLinearParameter(
            onJointSideCandidates, dir, ref, referencePlane, isAuxiliary,
        );
    }

    static intersectSplineParabola(spline: SketchSplineObject, parabola: SketchParabolaObject, parabolaJoint: IntersectJointContext,
        isTwoEntityClosedLoop: boolean,
    ): SketchPointObject[] {
        const referencePlane = spline.referencePlane;
        // 样条对象无 isAuxiliaryObject 协议（该属性仅线段/控制点持有），交点控制点默认非辅助对象
        const isAuxiliary = false;

        // ① 求解全部候选交点（可见区间过滤与去重已在 compute 内完成；
        //    参数 = 抛物线参数 t）
        const candidates = SketchEntityIntersect.computeSplineParabolaIntersections(spline, parabola);

        // ② 延伸方向与出发点由接缝上下文直接给出（不再靠 origin 端点匹配反推）：
        //    延伸方向由参数区间走向决定：spanDirection = Forward（end ≥ start）时终点侧沿参数
        //    增大方向、起点侧沿参数减小方向；端点反向越过（spanDirection = Backward）时相反。
        //    注意：spanDirection 由当前（可能已延长）抛物线定义计算，延长只按区间走向
        //    增减参数，不改变区间走向符号
        const definition = parabola.getParabolaDefinition();
        const spanDirection: IntersectDirection =
            definition.endParameter >= definition.startParameter
                ? IntersectDirection.Forward
                : IntersectDirection.Backward;
        const dir: IntersectDirection = parabolaJoint.side === 'end'
            ? spanDirection
            : SketchEntityNewControlPoint.reverseDirection(spanDirection);
        // 原始起止参数（延长前；未提供时回退到当前定义）
        const originStart = parabolaJoint.originStartParameter ?? definition.startParameter;
        const originEnd = parabolaJoint.originEndParameter ?? definition.endParameter;
        const jointParam = parabolaJoint.side === 'end' ? originEnd : originStart;
        const freeParam = parabolaJoint.side === 'end' ? originStart : originEnd;

        // ③ 接缝侧候选过滤：剔除位于非相连端一侧的交点
        const onJointSideCandidates = SketchEntityNewControlPoint.filterLinearCandidatesByJointSide(
            candidates, jointParam, freeParam, isTwoEntityClosedLoop,
        );

        // ④ 按延伸方向选取第一个交点并包装为控制点
        return SketchEntityNewControlPoint.pickFirstIntersectionByLinearParameter(
            onJointSideCandidates, dir, jointParam, referencePlane, isAuxiliary,
        );
    }

    static intersectParabolaLine(parabola: SketchParabolaObject, line: SketchLineObject, lineJoint: IntersectJointContext,
        isTwoEntityClosedLoop: boolean,
    ): SketchPointObject[] {
        return SketchEntityNewControlPoint.intersectLineParabola(line, parabola, lineJoint, isTwoEntityClosedLoop);
    }

    static intersectParabolaArc(parabola: SketchParabolaObject, arc: SketchArcObject, parabolaJoint: IntersectJointContext,
        isTwoEntityClosedLoop: boolean,
    ): SketchPointObject[] {
        return SketchEntityNewControlPoint.intersectArcParabola(arc, parabola, parabolaJoint, isTwoEntityClosedLoop);
    }

    static intersectParabolaEllipse(parabola: SketchParabolaObject, ellipse: SketchEllipseObject, parabolaJoint: IntersectJointContext,
        isTwoEntityClosedLoop: boolean,
    ): SketchPointObject[] {
        return SketchEntityNewControlPoint.intersectEllipseParabola(ellipse, parabola, parabolaJoint, isTwoEntityClosedLoop);
    }

    static intersectParabolaSpline(parabola: SketchParabolaObject, spline: SketchSplineObject, splineJoint: IntersectJointContext,
        isTwoEntityClosedLoop: boolean,
    ): SketchPointObject[] {
        return SketchEntityNewControlPoint.intersectSplineParabola(spline, parabola, splineJoint, isTwoEntityClosedLoop);
    }

    static intersectParabolaParabola(parabola1: SketchParabolaObject, parabola2: SketchParabolaObject, parabola1Joint: IntersectJointContext,
        isTwoEntityClosedLoop: boolean,
    ): SketchPointObject[] {
        const referencePlane = parabola1.referencePlane;
        // 抛物线对象无 isAuxiliaryObject 协议（该属性仅线段/控制点持有），交点控制点默认非辅助对象
        const isAuxiliary = false;

        // ① 求解全部候选交点（可见区间过滤与去重已在 compute 内完成；
        //    参数 = 交点在 parabola1 上的侧向参数 t1）
        const candidates = SketchEntityIntersect.computeParabolaParabolaIntersections(parabola1, parabola2);

        // ② 延伸方向与出发点由接缝上下文直接给出（不再靠 origin 端点匹配反推）：
        //    延伸方向由参数区间走向决定：spanDirection = Forward（end ≥ start）时终点侧沿参数
        //    增大方向、起点侧沿参数减小方向；端点反向越过（spanDirection = Backward）时相反。
        //    注意：spanDirection 由当前（可能已延长）抛物线定义计算，延长只按区间走向
        //    增减参数，不改变区间走向符号
        const definition = parabola1.getParabolaDefinition();
        const spanDirection: IntersectDirection =
            definition.endParameter >= definition.startParameter
                ? IntersectDirection.Forward
                : IntersectDirection.Backward;
        const dir: IntersectDirection = parabola1Joint.side === 'end'
            ? spanDirection
            : SketchEntityNewControlPoint.reverseDirection(spanDirection);
        // 原始起止参数（延长前；未提供时回退到当前定义）
        const originStart = parabola1Joint.originStartParameter ?? definition.startParameter;
        const originEnd = parabola1Joint.originEndParameter ?? definition.endParameter;
        const jointParam = parabola1Joint.side === 'end' ? originEnd : originStart;
        const freeParam = parabola1Joint.side === 'end' ? originStart : originEnd;

        // ③ 接缝侧候选过滤：剔除位于非相连端一侧的交点
        const onJointSideCandidates = SketchEntityNewControlPoint.filterLinearCandidatesByJointSide(
            candidates, jointParam, freeParam, isTwoEntityClosedLoop,
        );

        // ④ 按延伸方向选取第一个交点并包装为控制点
        return SketchEntityNewControlPoint.pickFirstIntersectionByLinearParameter(
            onJointSideCandidates, dir, jointParam, referencePlane, isAuxiliary,
        );
    }
}
